/* eslint-disable react/jsx-key */
//React and generic dependencies:
import React, { lazy } from "react";
import { useTheme } from "@mui/material";
import FeatureFlagRoute from "../containers/Page/Permissions/FeatureFlagRoute";
import AuthorizedRoute from "containers/Page/Permissions/AuthorizedRoute";
import AuthorizedComponent from "containers/Page/Permissions/AuthorizeComponent";

import SigilTabPage from "containers/Page/SigilTabPage";
import MenuTabDefs from "./menuTabDefs";
import { useFeatureFlag } from "hooks/useFeatureFlag";

import { FeatureFlags } from "types/featureFlags";
import SigilTablessPage from "containers/Page/SigilTablessPage";
// Gating routes
const GatingNew = lazy(() => import("pages/Gating/New/GatingNew"));
const GatingStarted = lazy(() =>
  import("../pages/Gating/Started/GatingStarted")
);
const GatingPending = lazy(() =>
  import("../pages/Gating/Pending/GatingPending")
);
const GatingClosed = lazy(() => import("../pages/Gating/Closed/GatingClosed"));
const GatingMaster = lazy(() => import("../pages/Gating/Master/GatingMaster"));

// Map routes
const MapNew = lazy(() => import("../pages/Map/Main/Map"));
const MapTemplates = lazy(() => import("pages/Map/Templates/MapTemplates"));

// Sellers/Products/Settings/Account.
const Sellers = lazy(() => import("../pages/Sellers/SigilSellerGrid"));
const Products = lazy(() => import("../pages/Products/SigilProductGrid"));
const Configs = lazy(() => import("../pages/ControlPanel"));
// const Billing = lazy(() => import("../pages/Account/Billing"));
const Profile = lazy(() => import("pages/Account/Profile"));

const Dashboard = lazy(() => import("../pages/Dashboard/Dashboard"));

import { selfServeRouteObjects } from "pages/SelfServe";

// Definitions
const { gating, warnings, sellers, products, settings, account } = MenuTabDefs;

// Tab definitions
const { tabs: gatingTabs } = gating;
const { tabs: warningsTabs } = warnings;
const { tabs: sellersTabs } = sellers;
const { tabs: productTabs } = products;
const { tabs: settingsTabs } = settings;
const { tabs: accountTabs } = account;

//subtitle definitions
// const gatingSub = gating.subtitle;
// console.log("sub", gatingSub);

const GetGatingParentElement = () => {
  const isCollapsedTabsEnabled = useFeatureFlag(FeatureFlags.COLLAPSED_TABS);
  return (
    <AuthorizedRoute>
      {isCollapsedTabsEnabled ? (
        <SigilTablessPage
          title={gating.title}
          subtitle={gating.subtitle}
          Actions={gating.actions}
          actionOptions={gating.actionOptions}
        >
          <GatingMaster />
        </SigilTablessPage>
      ) : (
        <SigilTabPage
          tabs={[
            gatingTabs.new,
            gatingTabs.openCases,
            gatingTabs.pending,
            gatingTabs.closed,
          ]}
          title={gating.title}
          subtitle={gating.subtitle}
          Actions={gating.actions}
          actionOptions={gating.actionOptions}
        ></SigilTabPage>
      )}
    </AuthorizedRoute>
  );
};

const buildParentElement = (
  { title, subtitle, actions, actionOptions },
  tabDefinitions
) => {
  return (
    <AuthorizedRoute>
      <SigilTabPage
        tabs={tabDefinitions}
        title={title}
        subtitle={subtitle}
        Actions={actions}
        actionOptions={actionOptions}
      ></SigilTabPage>
    </AuthorizedRoute>
  );
};

const buildChildElement = (tabDefinition, TabComponent) => {
  return (
    <AuthorizedComponent {...tabDefinition}>
      <TabComponent />
    </AuthorizedComponent>
  );
};

const getChildPath = ({ tabTitle, path }) => {
  return path || tabTitle;
};

const DashboardSubtitle = () => {
  const theme = useTheme();

  return (
    <SigilTablessPage
      title="Dashboard"
      subtitle={
        <>
          Track every action Sigil takes to protect your brand, and see the
          results. If you have questions, please reach out to{" "}
          <a
            href="mailto:customer@sigilprotects.com"
            style={{
              color: "#000000",
              fontWeight: 600,
              textDecoration: "underline",
              textDecorationColor: theme.palette.primary.main,
            }}
          >
            customer@sigilprotects.com
          </a>
          .
        </>
      }
      Actions={null}
      actionOptions={{}}
    >
      <Dashboard />
    </SigilTablessPage>
  );
};

const routes = [
  {
    path: gating.path,
    element: <GetGatingParentElement />,
    children: [
      {
        path: getChildPath(gatingTabs.new),
        element: buildChildElement(gatingTabs.new, GatingNew),
      },
      {
        path: getChildPath(gatingTabs.openCases),
        element: buildChildElement(gatingTabs.openCases, GatingStarted),
      },
      {
        path: getChildPath(gatingTabs.pending),
        element: buildChildElement(gatingTabs.pending, GatingPending),
      },
      {
        path: getChildPath(gatingTabs.closed),
        element: buildChildElement(gatingTabs.closed, GatingClosed),
      },
    ],
  },
  {
    path: "/Dashboard",
    element: (
      <AuthorizedRoute>
        <FeatureFlagRoute
          flagName={FeatureFlags.ACTIVITY_DASHBOARD}
          fallback="/Gating/New"
        >
          <DashboardSubtitle />
        </FeatureFlagRoute>
      </AuthorizedRoute>
    ),
  },
  {
    path: warnings.path,
    element: buildParentElement(warnings, [
      warningsTabs.new,
      warningsTabs.templates,
    ]),
    children: [
      {
        path: getChildPath(warningsTabs.new),
        element: buildChildElement(warningsTabs.new, MapNew),
      },
      {
        path: getChildPath(warningsTabs.templates),
        element: buildChildElement(warningsTabs.templates, MapTemplates),
      },
    ],
  },
  {
    path: sellers.path,
    element: buildParentElement(sellers, [
      sellersTabs.unauthorized,
      sellersTabs.authorized,
    ]),
    children: [
      {
        path: getChildPath(sellersTabs.authorized),
        element: buildChildElement(sellersTabs.authorized, Sellers),
      },
      {
        path: getChildPath(sellersTabs.unauthorized),
        element: buildChildElement(sellersTabs.unauthorized, Sellers),
      },
    ],
  },
  {
    path: products.path,
    element: buildParentElement(products, [productTabs.all]),
    children: [
      {
        path: getChildPath(productTabs.all),
        element: buildChildElement(productTabs.all, Products),
      },
    ],
  },
  {
    path: settings.path,
    element: (
      <AuthorizedRoute>
        <SigilTablessPage
          title={settings.title}
          subtitle={settings.subtitle}
          Actions={settings.actions}
          actionOptions={settings.actionOptions}
        >
          <Configs />
        </SigilTablessPage>
      </AuthorizedRoute>
    ),
    children: [
      {
        path: getChildPath(settingsTabs.configs),
        element: buildChildElement(settingsTabs.configs, Configs),
      },
    ],
  },
  {
    path: account.path,
    element: (
      <AuthorizedRoute>
        <SigilTablessPage
          title={account.title}
          subtitle={account.subtitle}
          Actions={account.actions}
          actionOptions={account.actionOptions}
        >
          <Profile />
        </SigilTablessPage>
      </AuthorizedRoute>
    ),
    children: [
      {
        path: getChildPath(accountTabs.profile),
        element: buildChildElement(accountTabs.profile, Profile),
      },
      // {
      //   path: getChildPath(accountTabs.billing),
      //   element: buildChildElement(accountTabs.billing, Billing),
      // },
    ],
  },
  ...selfServeRouteObjects,
];

export default routes;
